import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { VAULTS } from "@/lib/config";
import { useAuth } from "@/context/AuthContext";
import { useChatNotify } from "@/context/ChatNotifyContext";
import { useSpaces } from "@/context/SpaceContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import CategoryManager from "@/components/CategoryManager";
import { registerServiceWorker, enablePush } from "@/lib/push";
import AppLock from "@/components/AppLock";
import InstallPrompt from "@/components/InstallPrompt";
import { toast } from "sonner";

const Icon = ({ name, ...props }) => {
  const C = Icons[name] || Icons.Circle;
  return <C {...props} />;
};

function CategorySwitcher({ onManage, onNavigate }) {
  const { spaces, activeSpace, activeSpaceId, setActiveSpaceId } = useSpaces();
  return (
    <div className="px-3 pt-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="category-switcher"
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 bg-white hover:border-gray-300 transition-colors text-left">
            <Icons.Folder className="w-4 h-4 text-[#1B4332] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Category</div>
              <div className="text-sm font-medium truncate">{activeSpace?.name || "Select category"}</div>
            </div>
            <Icons.ChevronsUpDown className="w-4 h-4 text-gray-400 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-60" align="start" data-testid="category-switcher-menu">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-gray-400">Switch category</DropdownMenuLabel>
          {spaces.map((s) => (
            <DropdownMenuItem key={s.id} data-testid={`switch-category-${s.id}`}
              onSelect={() => { setActiveSpaceId(s.id); }}
              className="gap-2">
              <Icons.Folder className="w-4 h-4 text-[#1B4332]" />
              <span className="flex-1 truncate">{s.name}</span>
              {s.id === activeSpaceId && <Icons.Check className="w-4 h-4 text-[#1B4332]" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="manage-categories-btn" onSelect={() => { onManage(); onNavigate?.(); }} className="gap-2 text-gray-600">
            <Icons.Settings2 className="w-4 h-4" /> Manage categories
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function SidebarContent({ user, onNavigate, onLogout, chatUnread, onManage }) {
  const navItem = (to, icon, label, testid, badge) => (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={testid}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
          isActive ? "bg-[#0A0A0A] text-white" : "text-gray-600 hover:bg-gray-100"
        }`
      }
    >
      <Icon name={icon} className="w-[18px] h-[18px]" strokeWidth={2} />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span
          data-testid="chat-unread-badge"
          className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#B91C1C] text-white text-[11px] font-semibold flex items-center justify-center"
        >
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </NavLink>
  );

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="px-5 py-5 border-b border-gray-200 flex items-center gap-2.5">
        <img src="/renova-icon.png" alt="Renova Eco Systems" className="w-10 h-10 object-contain" />
        <div>
          <div className="font-heading font-extrabold text-[14px] leading-none tracking-tight">Renova Eco Systems</div>
          <div className="text-[11px] text-gray-500 mt-1">Digital Vault</div>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto vault-scroll">
        {navItem("/", "LayoutDashboard", "Dashboard", "nav-dashboard")}
        {navItem("/chat", "MessagesSquare", "Messages", "nav-chat", chatUnread)}
        {navItem("/directory", "Contact", "Directory", "nav-directory")}
        {navItem("/map", "Share2", "Company Map", "nav-map")}
        <CategorySwitcher onManage={onManage} onNavigate={onNavigate} />
        <div className="pt-4 pb-1.5 px-3 text-[11px] uppercase tracking-wider font-semibold text-gray-400">Vaults</div>
        {Object.entries(VAULTS).map(([key, v]) => (
          <React.Fragment key={key}>{navItem(`/vault/${key}`, v.icon, v.label, `nav-${key}`)}</React.Fragment>
        ))}
        <div className="pt-4 pb-1.5 px-3 text-[11px] uppercase tracking-wider font-semibold text-gray-400">Account</div>
        {navItem("/storage", "HardDrive", "My Storage", "nav-storage")}
        {user?.role === "admin" && navItem("/admin/users", "Users", "Users & Roles", "nav-admin-users")}
        {navItem("/settings", "Settings", "Settings", "nav-settings")}
      </nav>

      <div className="p-3 border-t border-gray-200">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-[#1B4332] text-white flex items-center justify-center text-sm font-semibold">
            {(user?.name || "U")[0].toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-gray-500 truncate">{user?.email}</div>
          </div>
        </div>
        <Button variant="ghost" className="w-full justify-start text-gray-600 mt-1" data-testid="logout-btn" onClick={onLogout}>
          <Icons.LogOut className="w-4 h-4 mr-2" /> Sign out
        </Button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { unread } = useChatNotify();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pushBanner, setPushBanner] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await registerServiceWorker();
      if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) return;
      if (Notification.permission === "granted") { enablePush(); return; }
      if (!cancelled) setPushBanner(true); // "default" or "denied" — nudge to enable
    })();
    return () => { cancelled = true; };
  }, []);

  const enableNotifications = async () => {
    const r = await enablePush();
    if (r.ok) { setPushBanner(false); toast.success("Notifications enabled"); }
    else if (r.reason === "denied") toast.error("Notifications are blocked. Enable them in your browser/site settings.");
    else if (r.reason === "unsupported") toast.error("On iPhone, add the app to your Home Screen first (iOS 16.4+).");
    else if (r.reason === "no-vapid") toast.error("Notifications aren't configured on the server yet.");
    else toast.error("Could not enable notifications.");
  };

  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-gray-200 flex-col fixed h-screen z-40">
        <SidebarContent user={user} onLogout={handleLogout} chatUnread={unread} onManage={() => setManageOpen(true)} />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="p-0 w-72 max-w-[85vw]" data-testid="mobile-sidebar">
          <SidebarContent user={user} onNavigate={() => setOpen(false)} onLogout={handleLogout} chatUnread={unread} onManage={() => setManageOpen(true)} />
        </SheetContent>
      </Sheet>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center gap-3 px-4 h-14 bg-white/90 backdrop-blur-xl border-b border-gray-200">
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" className="p-2 -ml-2 rounded-md hover:bg-gray-100 relative">
          <Icons.Menu className="w-5 h-5" />
          {unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-[#B91C1C]" />}
        </button>
        <img src="/renova-icon.png" alt="" className="w-7 h-7 object-contain" />
        <span className="font-heading font-extrabold text-sm tracking-tight">Renova Eco Systems</span>
      </header>

      <main className="lg:ml-64 min-h-screen bg-[#F9FAFB] overflow-x-hidden">
        {pushBanner && (
          <div className="bg-[#1B4332] text-white px-4 py-2.5 flex items-center gap-3 text-sm" data-testid="push-banner">
            <Icons.Bell className="w-4 h-4 shrink-0" />
            <span className="flex-1 min-w-0">Turn on notifications to get new message alerts.</span>
            <Button size="sm" variant="secondary" className="h-7 shrink-0" onClick={enableNotifications} data-testid="push-banner-enable">Enable</Button>
            <button onClick={() => setPushBanner(false)} data-testid="push-banner-dismiss" className="p-1 shrink-0 hover:opacity-80"><Icons.X className="w-4 h-4" /></button>
          </div>
        )}
        {children}
      </main>

      <AppLock />
      <InstallPrompt />
      <CategoryManager open={manageOpen} onClose={() => setManageOpen(false)} />
    </div>
  );
}
